'use client';

import React, { useState, useEffect } from 'react';
import { FileText, PlaySquare, Eye, MessageCircle, TrendingUp, TrendingDown, AlertCircle, Repeat2, Timer, ShieldCheck, Zap, Clock, CheckCircle2, XCircle, CalendarClock } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface MetricCardProps {
  title: string;
  value: string;
  trend: string;
  trendUp: boolean;
  trendNeutral?: boolean;
  icon: React.ElementType;
  iconColor: string;
  breakdown: { label: string; value: string; color?: string }[];
  alert?: boolean;
  featured?: boolean;
  liveTag?: React.ReactNode;
}

function MetricCard({
  title,
  value,
  trend,
  trendUp,
  trendNeutral,
  icon: Icon,
  iconColor,
  breakdown,
  alert,
  liveTag,
}: MetricCardProps) {
  return (
    <div
      className={`card-elevated p-5 flex flex-col gap-4 transition-all duration-200 hover:border-border/80 ${
        alert ? 'border-warning/30 bg-warning-muted/10' : ''
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className={`p-1.5 rounded-md ${iconColor}`}>
              <Icon size={15} />
            </div>
            <span className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
              {title}
            </span>
            {alert && <AlertCircle size={13} className="text-warning" />}
          </div>
          <span className="text-3xl font-bold tabular-nums text-foreground leading-none mt-1">
            {value}
          </span>
          {liveTag && <div className="mt-1">{liveTag}</div>}
        </div>
        <div
          className={`flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full shrink-0 ml-2 ${
            trendNeutral
              ? 'bg-muted text-muted-foreground'
              : trendUp
              ? 'bg-positive/10 text-positive' :'bg-negative/10 text-negative'
          }`}
        >
          {trendNeutral ? null : trendUp ? (
            <TrendingUp size={11} />
          ) : (
            <TrendingDown size={11} />
          )}
          {trend}
        </div>
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1.5 border-t border-border pt-3">
        {breakdown.map((item) => (
          <div key={`breakdown-${title}-${item.label}`} className="flex items-center gap-1.5">
            <span
              className={`text-xs font-mono tabular-nums font-semibold ${
                item.color ?? 'text-foreground'
              }`}
            >
              {item.value}
            </span>
            <span className="text-xs text-muted-foreground">{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Live webhook stats card
interface WebhookKpiCardProps {
  eventCount: number;
  lastSyncAt: Date | null;
  isLive: boolean;
}

function formatRelativeTime(date: Date | null): string {
  if (!date) return 'Never';
  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 5) return 'Just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  return `${diffHr}h ago`;
}

function WebhookKpiCard({ eventCount, lastSyncAt, isLive }: WebhookKpiCardProps) {
  const [relTime, setRelTime] = useState(() => formatRelativeTime(lastSyncAt));

  useEffect(() => {
    setRelTime(formatRelativeTime(lastSyncAt));
    const id = setInterval(() => setRelTime(formatRelativeTime(lastSyncAt)), 5000);
    return () => clearInterval(id);
  }, [lastSyncAt]);

  return (
    <div className="card-elevated p-5 flex flex-col gap-4 transition-all duration-200 hover:border-border/80 border-primary/20">
      <div className="flex items-start justify-between">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="p-1.5 rounded-md bg-primary/10 text-primary">
              <Zap size={15} />
            </div>
            <span className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
              Webhook Events
            </span>
            {/* Live pulse indicator */}
            <span className="flex items-center gap-1">
              <span
                className={`inline-block w-1.5 h-1.5 rounded-full ${
                  isLive ? 'bg-positive animate-pulse' : 'bg-muted-foreground'
                }`}
              />
              <span className={`text-[10px] font-semibold uppercase tracking-wider ${isLive ? 'text-positive' : 'text-muted-foreground'}`}>
                {isLive ? 'Live' : 'Offline'}
              </span>
            </span>
          </div>
          <span className="text-3xl font-bold tabular-nums text-foreground leading-none mt-1">
            {eventCount.toLocaleString()}
          </span>
        </div>
        <div className="flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full bg-primary/10 text-primary shrink-0 ml-2">
          <TrendingUp size={11} />
          Meta Flow
        </div>
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1.5 border-t border-border pt-3">
        <div className="flex items-center gap-1.5">
          <Clock size={11} className="text-muted-foreground" />
          <span className="text-xs text-muted-foreground">Last sync:</span>
          <span className="text-xs font-mono font-semibold text-foreground">{relTime}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-mono tabular-nums font-semibold text-info">{eventCount}</span>
          <span className="text-xs text-muted-foreground">total received</span>
        </div>
      </div>
    </div>
  );
}

// ─── Publishing Health KPI Card ───────────────────────────────────────────────
interface PublishHealthStats {
  successRate: number;       // 0-100
  failureCount: number;
  queueDepth: number;
  successCount: number;
  totalAttempts: number;
  loading: boolean;
}

function PublishHealthCard() {
  const [stats, setStats] = useState<PublishHealthStats>({
    successRate: 0,
    failureCount: 0,
    queueDepth: 0,
    successCount: 0,
    totalAttempts: 0,
    loading: true,
  });

  useEffect(() => {
    const load = async () => {
      try {
        // Attempt to pull live counts from localStorage publish log
        const raw = localStorage.getItem('publish_health');
        if (raw) {
          const parsed = JSON.parse(raw) as {
            successCount?: number;
            failureCount?: number;
            queueDepth?: number;
            updatedAt?: string;
          };
          const s = parsed.successCount ?? 0;
          const f = parsed.failureCount ?? 0;
          const q = parsed.queueDepth ?? 0;
          const total = s + f;
          setStats({
            successRate: total > 0 ? Math.round((s / total) * 100) : 100,
            failureCount: f,
            queueDepth: q,
            successCount: s,
            totalAttempts: total,
            loading: false,
          });
          return;
        }
      } catch { /* ignore */ }

      // Fallback: derive from reels grid state stored in localStorage
      try {
        const raw = localStorage.getItem('reel_publish_states');
        if (raw) {
          const states = JSON.parse(raw) as Record<string, string>;
          const vals = Object.values(states);
          const s = vals.filter((v) => v === 'success').length;
          const f = vals.filter((v) => v === 'failed').length;
          const q = vals.filter((v) => v === 'idle' || v === 'publishing').length;
          const total = s + f;
          setStats({
            successRate: total > 0 ? Math.round((s / total) * 100) : 100,
            failureCount: f,
            queueDepth: q,
            successCount: s,
            totalAttempts: total,
            loading: false,
          });
          return;
        }
      } catch { /* ignore */ }

      // Default healthy state when no data yet
      setStats({
        successRate: 100,
        failureCount: 0,
        queueDepth: 0,
        successCount: 0,
        totalAttempts: 0,
        loading: false,
      });
    };

    load();

    // Re-check every 30 s so the card stays fresh
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, []);

  // Also react to publish events dispatched by RecentReelsGrid
  useEffect(() => {
    const handler = () => {
      try {
        const raw = localStorage.getItem('publish_health');
        if (!raw) return;
        const parsed = JSON.parse(raw) as { successCount?: number; failureCount?: number; queueDepth?: number };
        const s = parsed.successCount ?? 0;
        const f = parsed.failureCount ?? 0;
        const q = parsed.queueDepth ?? 0;
        const total = s + f;
        setStats({
          successRate: total > 0 ? Math.round((s / total) * 100) : 100,
          failureCount: f,
          queueDepth: q,
          successCount: s,
          totalAttempts: total,
          loading: false,
        });
      } catch { /* ignore */ }
    };
    window.addEventListener('publish:update', handler);
    return () => window.removeEventListener('publish:update', handler);
  }, []);

  const rateColor =
    stats.successRate >= 90
      ? 'text-positive'
      : stats.successRate >= 70
      ? 'text-warning' :'text-negative';

  const hasFailures = stats.failureCount > 0;

  return (
    <div
      className={`card-elevated p-5 flex flex-col gap-4 transition-all duration-200 hover:border-border/80 ${
        hasFailures ? 'border-negative/20 bg-negative/5' : 'border-positive/20'
      }`}
    >
      {/* Header row */}
      <div className="flex items-start justify-between">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className={`p-1.5 rounded-md ${hasFailures ? 'bg-negative/10 text-negative' : 'bg-positive/10 text-positive'}`}>
              <CheckCircle2 size={15} />
            </div>
            <span className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
              Publish Health · 24h
            </span>
            {hasFailures && <AlertCircle size={13} className="text-negative" />}
          </div>

          {/* Success rate — big number */}
          <span className={`text-3xl font-bold tabular-nums leading-none mt-1 ${stats.loading ? 'text-muted-foreground' : rateColor}`}>
            {stats.loading ? '—' : `${stats.successRate}%`}
          </span>
        </div>

        {/* Badge */}
        <div
          className={`flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full shrink-0 ml-2 ${
            hasFailures ? 'bg-negative/10 text-negative' : 'bg-positive/10 text-positive'
          }`}
        >
          {hasFailures ? <XCircle size={11} /> : <CheckCircle2 size={11} />}
          {hasFailures ? 'Degraded' : 'Healthy'}
        </div>
      </div>

      {/* Breakdown row */}
      <div className="flex flex-wrap gap-x-4 gap-y-1.5 border-t border-border pt-3">
        {/* Success count */}
        <div className="flex items-center gap-1.5">
          <CheckCircle2 size={11} className="text-positive" />
          <span className="text-xs font-mono tabular-nums font-semibold text-positive">
            {stats.loading ? '—' : stats.successCount}
          </span>
          <span className="text-xs text-muted-foreground">published</span>
        </div>

        {/* Failure count */}
        <div className="flex items-center gap-1.5">
          <XCircle size={11} className={hasFailures ? 'text-negative' : 'text-muted-foreground'} />
          <span className={`text-xs font-mono tabular-nums font-semibold ${hasFailures ? 'text-negative' : 'text-muted-foreground'}`}>
            {stats.loading ? '—' : stats.failureCount}
          </span>
          <span className="text-xs text-muted-foreground">failed</span>
        </div>

        {/* Queue depth */}
        <div className="flex items-center gap-1.5">
          <CalendarClock size={11} className="text-warning" />
          <span className="text-xs font-mono tabular-nums font-semibold text-warning">
            {stats.loading ? '—' : stats.queueDepth}
          </span>
          <span className="text-xs text-muted-foreground">queued</span>
        </div>
      </div>
    </div>
  );
}

const metrics: MetricCardProps[] = [
  {
    title: 'Total Scripts',
    value: '47',
    trend: '+6 today',
    trendUp: true,
    icon: FileText,
    iconColor: 'bg-primary/10 text-primary',
    breakdown: [
      { label: 'draft', value: '12', color: 'text-muted-foreground' },
      { label: 'ready', value: '8', color: 'text-positive' },
      { label: 'rendering', value: '3', color: 'text-info' },
      { label: 'queued', value: '5', color: 'text-warning' },
    ],
  },
  {
    title: 'Published Reels',
    value: '214',
    trend: '+3 today',
    trendUp: true,
    icon: PlaySquare,
    iconColor: 'bg-accent/10 text-accent',
    breakdown: [
      { label: 'in queue', value: '5', color: 'text-warning' },
      { label: 'this week', value: '21', color: 'text-foreground' },
      { label: 'this month', value: '89', color: 'text-foreground' },
    ],
  },
  {
    title: 'Total Views',
    value: '1.84M',
    trend: '+12.4%',
    trendUp: true,
    icon: Eye,
    iconColor: 'bg-violet-500/10 text-violet-400',
    breakdown: [
      { label: 'saves', value: '24.2K', color: 'text-positive' },
      { label: 'shares', value: '8.7K', color: 'text-info' },
      { label: 'reach today', value: '41K', color: 'text-foreground' },
    ],
  },
  {
    title: 'DM Conversions',
    value: '312',
    trend: '-2.1%',
    trendUp: false,
    icon: MessageCircle,
    iconColor: 'bg-warning/10 text-warning',
    alert: true,
    breakdown: [
      { label: 'sent', value: '7,841', color: 'text-foreground' },
      { label: 'filtered', value: '1,203', color: 'text-muted-foreground' },
      { label: 'rules active', value: '4', color: 'text-info' },
    ],
  },
  {
    title: 'Gen → Published Rate',
    value: '68.1%',
    trend: '+4.3%',
    trendUp: true,
    icon: Repeat2,
    iconColor: 'bg-teal-500/10 text-teal-400',
    breakdown: [
      { label: 'generated', value: '314', color: 'text-foreground' },
      { label: 'published', value: '214', color: 'text-positive' },
      { label: 'dropped', value: '100', color: 'text-negative' },
    ],
  },
  {
    title: 'Avg Render Time',
    value: '2m 14s',
    trend: '-18s',
    trendUp: true,
    icon: Timer,
    iconColor: 'bg-sky-500/10 text-sky-400',
    breakdown: [
      { label: 'fastest', value: '0m 48s', color: 'text-positive' },
      { label: 'slowest', value: '6m 02s', color: 'text-negative' },
      { label: 'jobs today', value: '31', color: 'text-foreground' },
    ],
  },
  {
    title: 'Approval Pass Rate',
    value: '91.4%',
    trend: '+1.8%',
    trendUp: true,
    icon: ShieldCheck,
    iconColor: 'bg-emerald-500/10 text-emerald-400',
    breakdown: [
      { label: 'approved', value: '287', color: 'text-positive' },
      { label: 'rejected', value: '27', color: 'text-negative' },
      { label: 'pending review', value: '9', color: 'text-warning' },
    ],
  },
];

export default function MetricsBentoGrid() {
  const [webhookStats, setWebhookStats] = useState<{ eventCount: number; lastSyncAt: Date | null; isLive: boolean }>({
    eventCount: 0,
    lastSyncAt: null,
    isLive: false,
  });

  // Load persisted count from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem('webhook_stats');
      if (raw) {
        const parsed = JSON.parse(raw);
        setWebhookStats((prev) => ({
          ...prev,
          eventCount: parsed.eventCount ?? 0,
          lastSyncAt: parsed.lastSyncAt ? new Date(parsed.lastSyncAt) : null,
        }));
      }
    } catch {
      // ignore
    }
  }, []);

  // Poll the webhook stats endpoint every 30s
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await fetch('/api/meta/webhook?hub.mode=stats');
        if (res.ok) {
          const data = await res.json();
          const now = new Date();
          const serverCount: number = data.totalWebhookEvents ?? 0;
          const serverLastAt: Date | null = data.lastEventAt ? new Date(data.lastEventAt) : null;

          setWebhookStats((prev) => {
            // Use whichever count is higher (server resets on cold start)
            const count = Math.max(prev.eventCount, serverCount);
            const lastSyncAt = serverLastAt ?? prev.lastSyncAt ?? now;
            try {
              localStorage.setItem('webhook_stats', JSON.stringify({ eventCount: count, lastSyncAt: lastSyncAt.toISOString() }));
            } catch { /* ignore */ }
            return { eventCount: count, lastSyncAt, isLive: true };
          });
        } else {
          setWebhookStats((prev) => ({ ...prev, isLive: false }));
        }
      } catch {
        setWebhookStats((prev) => ({ ...prev, isLive: false }));
      }
    };

    fetchStats();
    const id = setInterval(fetchStats, 30_000);
    return () => clearInterval(id);
  }, []);

  // Listen for webhook:event custom events dispatched by other parts of the app
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ count?: number }>).detail;
      const increment = detail?.count ?? 1;
      setWebhookStats((prev) => {
        const now = new Date();
        const next = { eventCount: prev.eventCount + increment, lastSyncAt: now, isLive: prev.isLive };
        try {
          localStorage.setItem('webhook_stats', JSON.stringify({ eventCount: next.eventCount, lastSyncAt: now.toISOString() }));
        } catch { /* ignore */ }
        return next;
      });
    };
    window.addEventListener('webhook:event', handler);
    return () => window.removeEventListener('webhook:event', handler);
  }, []);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
      {metrics.map((metric) => (
        <MetricCard key={`metric-${metric.title}`} {...metric} />
      ))}
      {/* Publishing Health KPI card */}
      <PublishHealthCard />
      {/* Live Webhook KPI card */}
      <WebhookKpiCard
        eventCount={webhookStats.eventCount}
        lastSyncAt={webhookStats.lastSyncAt}
        isLive={webhookStats.isLive}
      />
    </div>
  );
}